import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  TextField,
  Pagination,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemAvatar,
  ListItemText,
  // ListItemSecondaryAction removed (unused)
  Badge,
  Chip,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import collectionIconImg from '../images/collectionicon.png';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import { useWebSocket, useUser } from './WebSocketContext';
import { useSnackbar } from '../Utils/SnackBarContext';
import { useTranslation } from 'react-i18next';
import logo from '../images/logo-7-80x80.png';
import ActiveTasksStatus from './GlobalSettings/ActiveTasksStatus';
import CreateMenuButton from './CreateMenuButton';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * SidebarDrawer Component
 * 
 * The main sidebar drawer component that contains the navigation, search, and case list.
 * This component provides the primary navigation interface for the application.
 * 
 * Features:
 * - Responsive drawer that can be toggled open/closed
 * - Search functionality for filtering cases
 * - Pagination for large case lists
 * - Language switcher and settings access
 * - Case list with hover effects and context menus
 * - New events notification badges
 * - Smooth animations and modern styling
 */
const SidebarDrawer = ({
  isOpen,
  onToggle,
  cases,
  highlightCaseId = null,
  collections = [],
  collectionsLoading = false,
  selectedCollection,
  onCollectionChange,
  selectedCaseId,
  searchQuery,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  onCaseClick,
  onCaseMenuOpen,
  onToggleCaseIngestionPause = () => { },
  onLanguageMenuOpen,
  onLogout,
  isUserLoggedIn,
  isLoading = false,
  connectionError = false,
  onRetry,
  onCreateProject,
  onCreateCollection,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  selectedColor = '#4545a0',
  lightPurple = '#f0f0ff',
  storageStatus = {},
  restrictToUserFolder = false,
  storageDisplayPath = '/',
  storageFolders = [],
  storageFiles = [],
  storageBrowserLoading = false,
  storageBrowserError = '',
  onStorageFolderClick = () => { },
  onStorageNavigateUp = () => { },
  onStorageRefresh = () => { },
  onStorageOpen = () => { },
  credits = 0,
  tier = ''
}) => {
  const { t } = useTranslation();
  const { isReadOnly, permissions } = useUser();
  const isAdmin = permissions === 'admin';
  const canAccessGlobalSettings = permissions === 'admin' || permissions === 'editor';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [storageDownloadJob, setStorageDownloadJob] = React.useState({ active: false, preparing: false });
  const parseTimestamp = (value) => {
    if (!value) {
      return null;
    }
    const normalizedValue = typeof value === 'string'
      ? value.replace(' ', 'T')
      : value;
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatRelativeTime = (value, emptyLabel) => {
    const dateValue = parseTimestamp(value);
    if (!dateValue) {
      return emptyLabel;
    }
    const diffMs = Date.now() - dateValue.getTime();
    if (diffMs < 60000) {
      return t('sidebar.just_now', 'Just now');
    }
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days}d ago`;
    }
    return dateValue.toLocaleDateString();
  };

  const formatExactTimestamp = (value, emptyLabel) => {
    const dateValue = parseTimestamp(value);
    return dateValue ? dateValue.toLocaleString() : emptyLabel;
  };

  const formatBytes = (bytes) => {
    const parsed = Number(bytes);
    if (!parsed || parsed <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const exponent = Math.min(Math.floor(Math.log(parsed) / Math.log(1024)), units.length - 1);
    const value = parsed / 1024 ** exponent;
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[exponent]}`;
  };

  const getDescriptionPreview = (description) => {
    if (!description || !description.trim()) {
      return t('sidebar.no_description', 'No description available');
    }
    const trimmed = description.trim();
    return trimmed.length > 100 ? `${trimmed.slice(0, 97)}...` : trimmed;
  };

  const getCaseTooltipContent = (caseData) => {
    const description = caseData?.description?.trim();
    const goal = caseData?.goal?.trim();
    if (!description && !goal) {
      return t('sidebar.case_tooltip_empty', 'No details provided');
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 260 }}>
        {description && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              {t('sidebar.case_description_label', 'Description')}
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {description}
            </Typography>
          </Box>
        )}
        {goal && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              {t('sidebar.case_goal_label', 'Goal')}
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {goal}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Consolidate theme colors to match other components
  const themeColors = {
    primary: primaryColor,
    primaryLight: hoverColor,
    primaryDark: selectedColor,
    accent: '#f59e0b',
    text: '#374151',
    lightText: '#6b7280',
    background: '#ffffff',
    lightBackground: lightPurple,
    surface: 'rgba(255, 255, 255, 0.9)',
    surfaceBorder: 'rgba(87, 87, 176, 0.14)',
    surfaceShadow: '0 12px 30px rgba(20, 24, 74, 0.08)'
  };

  const storageData = storageStatus || {};
  const rawUsedPercent = storageData.usedPercent ?? storageData.used_percent ?? 0;
  const storageUsedPercent = Math.min(100, Math.max(0, Number(rawUsedPercent) || 0));
  const rawFreePercent = storageData.freePercent ?? storageData.free_percent ?? (100 - storageUsedPercent);
  const storageFreePercent = Math.min(100, Math.max(0, Number(rawFreePercent) || 0));
  const totalBytes = Math.max(0, Number(storageData.totalBytes ?? storageData.total_bytes ?? 0) || 0);
  const freeBytes = Math.max(0, Number(storageData.freeBytes ?? storageData.free_bytes ?? (totalBytes * (storageFreePercent / 100))) || 0);
  const usedBytes = Math.max(0, Number(storageData.usedBytes ?? storageData.used_bytes ?? (totalBytes - freeBytes)) || 0);
  const storageError = storageData.error;
  const isStorageLoading = Boolean(storageData.isLoading);

  const cardEntryAnimation = {
    opacity: 0,
    transform: 'translateY(-6px)',
    animation: 'sidebarCardEnter 380ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
    '@keyframes sidebarCardEnter': {
      '0%': { opacity: 0, transform: 'translateY(-8px)' },
      '60%': { opacity: 1, transform: 'translateY(3px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' }
    }
  };

  const headerActionButtonSx = {
    width: 24,
    height: 24,
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.25)',
    backgroundColor: 'rgba(255,255,255,0.12)',
    color: '#fff',
    boxShadow: '0 10px 20px rgba(0,0,0,0.16)',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.5s ease',
    position: 'relative',
    overflow: 'hidden',
    isolation: 'isolate',
    animation: 'headerButtonPulse 5.2s ease-in-out infinite',
    '@keyframes headerButtonPulse': {
      '0%': {
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        filter: 'brightness(0.95)'
      },
      '50%': {
        boxShadow: '0 16px 32px rgba(0,0,0,0.28)',
        filter: 'brightness(1.08)'
      },
      '100%': {
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        filter: 'brightness(0.95)'
      }
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'radial-gradient(circle, rgba(255,255,255,0.35), transparent 65%)',
      opacity: 0,
      transform: 'scale(0.4)',
      transition: 'transform 0.45s ease, opacity 0.45s ease',
      zIndex: -1
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.25)',
      transform: 'translateY(-2px) scale(1.05)',
      boxShadow: '0 14px 30px rgba(0,0,0,0.25)',
      '&::after': {
        opacity: 0.85,
        transform: 'scale(1.35)'
      }
    }
  };

  // Utilities
  const clearSearch = () => {
    if (typeof onSearchChange !== 'function') return;
    try { onSearchChange({ target: { value: '' } }); } catch (err) { try { onSearchChange(''); } catch (_) { } }
  };
  const handleCaseKeyDown = (e, caseId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCaseClick(caseId);
      onToggle();
    }
  };

  // limitText removed (UI uses CSS truncation instead)

  // Status dialog handlers
  const handleStatusDialogOpen = () => {
    setStatusDialogOpen(true);
  };

  const handleLogoClick = () => {
    window.location.href = '/';
    if (typeof onToggle === 'function') {
      onToggle();
    }
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  // Wrapper function to handle create project and close sidebar
  const handleCreateProject = () => {
    onCreateProject(); // Call the original function
    onToggle(); // Close the sidebar
  };

  // Wrapper function to handle create collection and close sidebar
  const handleCreateCollection = () => {
    onCreateCollection(); // Call the original function
    onToggle(); // Close the sidebar
  };

  // Avatar helper utilities to generate colored initials for cases (reduces repetitive folder icons)
  // Use a softer/pastel palette so avatars blend with the UI and don't feel heavy
  const avatarColors = ['#b39ddb', '#90caf9', '#a5d6a7', '#ef9a9a', '#ffcc80', '#80cbc4', '#b39ddb', '#ffab91', '#81d4fa', '#f48fb1'];
  const getColorFromString = (input) => {
    const s = String(input || '');
    if (!s) return avatarColors[0];
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = s.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  // Return two-character initials for better visual balance (e.g. "AB" instead of "A")
  const getInitials = (text) => {
    const s = String(text || '').trim();
    if (!s) return '?';
    const words = s.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      return (words[0][0] + (words[1][0] || '')).slice(0, 2).toUpperCase();
    }
    return s.slice(0, 2).toUpperCase();
  };
  const getAnimatedAvatarSx = (title) => ({
    width: { xs: 28, sm: 30, md: 32 },
    height: { xs: 28, sm: 30, md: 32 },
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
    bgcolor: getColorFromString(title),
    transition: 'transform 0.35s ease, box-shadow 0.35s ease',
    boxShadow: '0 4px 10px rgba(15, 23, 42, 0.12)',
    '.MuiListItemButton-root:hover &': {
      transform: 'translateX(2px) scale(1.05)',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.18)'
    }
  });

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onToggle}
      PaperProps={{
        sx: {
          width: { xs: '88vw', sm: 320, md: 360, lg: 380 },
          minWidth: { sm: 280 },
          maxWidth: { xs: '92vw', sm: 420 },
          transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'linear-gradient(to bottom, #ffffff, #f7f7ff)',
          backgroundSize: '180% 180%',
          animation: 'drawerGradientShift 26s ease-in-out infinite alternate, drawerLift 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both',
          boxShadow: `0 12px 32px ${primaryColor}20`,
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
          position: 'relative',
          isolation: 'isolate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: -2,
            borderRadius: 'inherit',
            background: `radial-gradient(circle at top, ${primaryColor}22, transparent 60%)`,
            opacity: 0.5,
            filter: 'blur(18px)',
            pointerEvents: 'none',
            animation: 'drawerGlow 8s ease-in-out infinite alternate'
          },
          '@keyframes drawerGradientShift': {
            '0%': { backgroundPosition: '0% 0%' },
            '100%': { backgroundPosition: '0% 90%' }
          },
          '@keyframes drawerLift': {
            '0%': { opacity: 0, transform: 'translateX(-16px)' },
            '100%': { opacity: 1, transform: 'translateX(0)' }
          },
          '@keyframes drawerGlow': {
            '0%': { opacity: 0.35 },
            '100%': { opacity: 0.7 }
          }
        }
      }}
    >
      <Box className="sidebar" sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {/* Header: logo + app title + quick actions (kept compact and consistent) */}
        <Box
          sx={{
            position: 'relative',
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 1.5, sm: 2, md: 2.25 },
            background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryLight} 45%, ${themeColors.primaryDark} 100%)`,
            color: '#fff',
            boxShadow: `0 18px 36px ${themeColors.primary}25`,
            flexShrink: 0,
            borderBottomLeftRadius: { xs: 16, sm: 24 },
            borderBottomRightRadius: { xs: 16, sm: 24 },
            overflow: 'hidden',
            boxSizing: 'border-box',
            '&:before': {
              content: '""',
              position: 'absolute',
              width: '160%',
              height: '160%',
              top: '-60%',
              left: '-40%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.4), transparent 60%)',
              opacity: 0.35,
              filter: 'blur(10px)'
            },
            '&:after': {
              content: '""',
              position: 'absolute',
              width: 220,
              height: 220,
              bottom: -120,
              right: -40,
              background: 'radial-gradient(circle, rgba(255,255,255,0.45), transparent 60%)',
              opacity: 0.45,
              filter: 'blur(6px)'
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 1.25, pr: { xs: 5, sm: 7 }, pt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
              <Box
                onClick={handleLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleLogoClick();
                  }
                }}
                sx={{
                  width: { xs: 36, sm: 44, md: 48 },
                  height: { xs: 36, sm: 44, md: 48 },
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  boxShadow: '0 14px 34px rgba(0,0,0,0.25)',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.03)',
                    backgroundColor: 'rgba(255,255,255,0.22)'
                  }
                }}
              >
                <img
                  src={logo}
                  alt="DataUnfold"
                  style={{ width: '68%', height: '68%', objectFit: 'contain' }}
                />
              </Box>
              <Box>
                <Typography variant={isMobile ? "body1" : "subtitle1"} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  DataUnfold
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.92, display: 'block' }}>
                  {t('sidebar.workspace', 'Workspace')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 18 },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              zIndex: 2
            }}
          >
            {canAccessGlobalSettings && (
              <Tooltip title={t('sidebar.active_tasks_status', 'Active Tasks Status')}>
                <IconButton size="small" onClick={handleStatusDialogOpen} sx={{ ...headerActionButtonSx, width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 } }}>
                  <PlayArrowIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('sidebar.settings', 'Settings')}>
              <IconButton
                component={Link}
                to="/Settings"
                onClick={onToggle}
                size="small"
                sx={{ ...headerActionButtonSx, width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 } }}
              >
                <SettingsIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('sidebar.change_language', 'Change language')}>
              <IconButton onClick={onLanguageMenuOpen} size="small" sx={{ ...headerActionButtonSx, width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 } }}>
                <LanguageIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              </IconButton>
            </Tooltip>
            {isUserLoggedIn && (
              <Tooltip title={t('sidebar.logout', 'Logout')}>
                <IconButton onClick={onLogout} size="small" sx={{ ...headerActionButtonSx, width: { xs: 22, sm: 24 }, height: { xs: 22, sm: 24 } }}>
                  <LogoutIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {isAdmin && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 40, sm: 52 },
                right: { xs: 8, sm: 18 },
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Tooltip
                title={
                  credits !== undefined && Number(credits) <= 0
                    ? "AI services are no longer working. The platform will become unresponsive when using functionalities that require AI."
                    : credits !== undefined && Number(credits) < 100
                      ? `Low Credits (${credits})`
                      : t('sidebar.credits_description', 'Remaining credits for Dataunfold models')
                }
                arrow
              >
                <Box
                  component={Link}
                  to="/purchase-credits"
                  onClick={() => {
                    if (typeof onToggle === 'function') {
                      onToggle();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'rgba(255,255,255,0.95)',
                    backgroundColor:
                      credits !== undefined && Number(credits) <= 0
                        ? 'rgba(211, 47, 47, 0.85)'
                        : credits !== undefined && Number(credits) < 100
                          ? 'rgba(237, 108, 2, 0.85)'
                          : 'rgba(0,0,0,0.2)',
                    padding: { xs: '1px 6px', sm: '2px 8px' },
                    borderRadius: '10px',
                    backdropFilter: 'blur(4px)',
                    border: credits !== undefined && Number(credits) < 100 ? '1px solid rgba(255,255,255,0.3)' : 'none',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                      backgroundColor: 'rgba(255,255,255,0.08)'
                    }
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: { xs: 12, sm: 16 }, color: '#FFD700' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                    {credits !== undefined ? credits : 0}
                  </Typography>
                </Box>
              </Tooltip>
              {tier && (
                <Chip
                  label={`Tier ${tier}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    ml: 1,
                    borderColor: 'rgba(255,255,255,0.6)',
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    bgcolor: 'rgba(255,255,255,0.06)',
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    px: { xs: 0.5, sm: 1 }
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Collection filter (moved out of header) */}
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: '16px',
              background: 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(87, 87, 176, 0.12)',
              ...cardEntryAnimation
            }}
          >
            <FormControl
              size="small"
              variant="outlined"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(87, 87, 176, 0.04)',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: `${themeColors.primary}33`
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.primary
                  }
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.5
                }
              }}
            >
              <Select
                value={selectedCollection || ''}
                displayEmpty
                onChange={(e) => onCollectionChange(e.target.value || null)}
                IconComponent={(props) => (
                  <KeyboardArrowDownIcon
                    {...props}
                    sx={{
                      color: themeColors.primary,
                      right: 10,
                      position: 'absolute'
                    }}
                  />
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 18px 30px rgba(20,24,74,0.16)',
                      mt: 0.5
                    }
                  }
                }}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Typography variant="body2" sx={{ color: themeColors.lightText }}>
                        {t('sidebar.all_projects_chip', 'All projects')}
                      </Typography>
                    );
                  }
                  const active = collections.find((c) => c.id === value);
                  return (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {active?.title || t('sidebar.untitled_collection', 'Untitled Collection')}
                    </Typography>
                  );
                }}
              >
                <MenuItem value="">
                  {t('sidebar.all_projects_chip', 'All projects')}
                </MenuItem>
                {collectionsLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      {t('sidebar.loading_collections', 'Loading collections...')}
                    </Box>
                  </MenuItem>
                ) : (
                  collections.map((collection) => (
                    <MenuItem key={collection.id} value={collection.id}>
                      {collection.title || t('sidebar.untitled_collection', 'Untitled Collection')}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {canAccessGlobalSettings && (
              <Tooltip title={t('sidebar.create_collection', 'Create collection')}>
                <IconButton
                  onClick={handleCreateCollection}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    background: themeColors.primary,
                    color: '#fff',
                    boxShadow: '0 8px 16px rgba(87, 87, 176, 0.2)',
                    transition: 'all 0.15s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: '-6px',
                      borderRadius: '14px',
                      border: `1px solid ${themeColors.primary}44`,
                      opacity: 0,
                      animation: 'sidebarPulse 3.2s ease-in-out infinite'
                    },
                    '@keyframes sidebarPulse': {
                      '0%': { opacity: 0, transform: 'scale(0.9)' },
                      '40%': { opacity: 0.4, transform: 'scale(1.05)' },
                      '100%': { opacity: 1, transform: 'scale(1.2)' }
                    },
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      background: hoverColor
                    }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Paper>
        </Box>

        {/* Search field with better styling */}
        <Box px={2} py={1.25} sx={{ flexShrink: 0, boxSizing: 'border-box', width: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '22px',
              border: `1px solid ${themeColors.surfaceBorder}`,
              background: themeColors.surface,
              backgroundSize: '200% 200%',
              boxShadow: themeColors.surfaceShadow,
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              position: 'relative',
              overflow: 'hidden',
              animation: 'searchGlow 16s ease-in-out infinite',
              ...cardEntryAnimation,
              animationDelay: '90ms',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 14px 34px rgba(20, 24, 74, 0.12)'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(120deg, transparent 20%, rgba(87,87,176,0.08) 40%, transparent 70%)',
                transform: 'translateX(-60%)',
                opacity: 0,
                pointerEvents: 'none',
                animation: 'searchSheen 8s ease-in-out infinite'
              },
              '@keyframes searchGlow': {
                '0%': { backgroundPosition: '0% 50%' },
                '50%': { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' }
              },
              '@keyframes searchSheen': {
                '0%, 60%': { opacity: 0, transform: 'translateX(-70%)' },
                '70%': { opacity: 0.4, transform: 'translateX(20%)' },
                '100%': { opacity: 0, transform: 'translateX(80%)' }
              }
            }}
          >
            <TextField
              placeholder={t('sidebar.search_placeholder') || 'Search Topics'}
              size="small"
              value={searchQuery}
              onChange={onSearchChange}
              variant="standard"
              fullWidth
              sx={{
                ml: 1,
                boxSizing: 'border-box',
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' }
              }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: themeColors.primary, ml: 0.5 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    aria-label={t('sidebar.clear_search', 'Clear search')}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '10px',
                      color: themeColors.primary,
                      '&:hover': {
                        backgroundColor: 'rgba(87, 87, 176, 0.08)'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
            />
          </Paper>
        </Box>



        {/* List of projects with improved styling */}
        <Box sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <List sx={{ px: 1, width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {/* Create menu moved to footer; top button removed */}

            {(() => {
              if (isLoading) {
                return (
                  <Box sx={{ p: 2 }}>
                    {[...Array(6)].map((_, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="text" width={'70%'} height={16} />
                      </Box>
                    ))}
                  </Box>
                );
              }

              if (connectionError) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', textAlign: 'center', fontSize: '0.85rem', mb: 1 }}>{t('sidebar.connection_error')}</Typography>
                    <Button variant="outlined" size="small" onClick={onRetry} sx={{ borderColor: primaryColor, color: primaryColor, '&:hover': { borderColor: hoverColor, backgroundColor: 'rgba(87, 87, 176, 0.04)' } }}>{t('sidebar.retry')}</Button>
                  </Box>
                );
              }

              if (cases.length === 0) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', textAlign: 'center', fontSize: '0.85rem' }}>{t('sidebar.no_cases')}</Typography>
                  </Box>
                );
              }

              return cases.map((caseData, index) => {
                const isPendingCopy = Boolean(caseData?.isPendingCopy);
                const isHighlighted = highlightCaseId !== null && String(highlightCaseId) === String(caseData.id);
                const isIngestionPaused = Boolean(caseData?.ingestionPaused);
                const ingestionStatus = String(caseData?.ingestionStatus || (isIngestionPaused ? 'paused' : 'active')).toLowerCase();
                const isIngestionPending = ingestionStatus === 'pending';
                const canToggleIngestion = caseData?.ingestionToggleAllowed !== false && !isIngestionPending;
                return (
                <ListItem
                  key={caseData.id}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    opacity: 0,
                    animation: 'projectItemEnter 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                    animationDelay: `${index * 0.05}s`,
                    '@keyframes projectItemEnter': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateX(-20px) scale(0.92)',
                        filter: 'blur(2px)'
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateX(0) scale(1)',
                        filter: 'blur(0px)'
                      }
                    }
                  }}
                >
                  <Tooltip
                    title={getCaseTooltipContent(caseData)}
                    placement="right"
                    arrow
                    enterDelay={400}
                  >
                    <ListItemButton
                      component={isPendingCopy ? 'div' : Link}
                      to={isPendingCopy ? undefined : `/case/${caseData.id}`}
                      onClick={() => {
                        if (isPendingCopy) return;
                        onCaseClick(caseData.id);
                        onToggle();
                      }}
                      onKeyDown={(e) => {
                        if (isPendingCopy) return;
                        handleCaseKeyDown(e, caseData.id);
                      }}
                      selected={!isPendingCopy && selectedCaseId === caseData.id}
                      disabled={isPendingCopy}
                      sx={{
                        borderRadius: '12px',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.75, sm: 1 },
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        ...(isHighlighted ? {
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          boxShadow: `0 0 0 2px ${themeColors.accent}55`,
                          animation: 'caseHighlightPulse 1.6s ease-in-out infinite',
                          '@keyframes caseHighlightPulse': {
                            '0%': { boxShadow: `0 0 0 2px ${themeColors.accent}55` },
                            '50%': { boxShadow: `0 0 0 6px ${themeColors.accent}25` },
                            '100%': { boxShadow: `0 0 0 2px ${themeColors.accent}55` }
                          }
                        } : {}),
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '3px',
                          height: '0%',
                          backgroundColor: themeColors.primary,
                          borderRadius: '0 4px 4px 0',
                          transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          boxShadow: `0 4px 12px ${themeColors.primary}20`,
                          '&::before': {
                            height: '70%',
                          }
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          fontWeight: 700,
                          '&::before': {
                            height: '70%',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(87, 87, 176, 0.15)',
                          }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        {caseData.hasNewEvents ? (
                          <Badge variant="dot" color="error">
                            <Avatar sx={getAnimatedAvatarSx(caseData.title)}>{getInitials(caseData.title)}</Avatar>
                          </Badge>
                        ) : (
                          <Avatar sx={getAnimatedAvatarSx(caseData.title)}>{getInitials(caseData.title)}</Avatar>
                        )}
                      </ListItemAvatar>

                      <ListItemText
                        primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{caseData.title}</Box>
                          {caseData.isPublic === 1 && (
                            <Tooltip title={t('sidebar.public_case', 'Public case (viewable without login)')}>
                              <VisibilityIcon sx={{ color: '#d32f2f', fontSize: '0.95rem' }} />
                            </Tooltip>
                          )}
                        </Box>}
                        primaryTypographyProps={{ fontWeight: selectedCaseId === caseData.id ? 700 : 500, fontSize: '0.95rem' }}
                        secondary={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, fontSize: '0.75rem', color: themeColors.lightText }}>
                            {isPendingCopy && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1.1, color: themeColors.accent }}
                              >
                                <CircularProgress size={10} sx={{ color: themeColors.accent }} />
                                {t('sidebar.duplicating', 'Duplicating...')}
                              </Typography>
                            )}
                            {(isIngestionPaused || isIngestionPending) && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1.1, color: themeColors.accent }}
                              >
                                <PauseCircleOutlineIcon sx={{ fontSize: '0.85rem' }} />
                                {isIngestionPending
                                  ? t('sidebar.project_pending', 'Pending')
                                  : t('sidebar.project_paused', 'Paused')}
                              </Typography>
                            )}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1.1 }}
                              title={formatExactTimestamp(caseData.last_visit || caseData.created_at, t('sidebar.never_used', 'Never opened'))}
                            >
                              {t('sidebar.last_used', 'Last used')}: {formatRelativeTime(caseData.last_visit || caseData.created_at, t('sidebar.never_used', 'Never opened'))}
                            </Typography>
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                lineHeight: 1.1,
                                color: themeColors.text
                              }}
                              title={caseData.description || t('sidebar.no_description', 'No description available')}
                            >
                              {getDescriptionPreview(caseData.description)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                        sx={{ minWidth: 0, ml: 0.5 }}
                      />

                      {!isReadOnly && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, alignSelf: 'flex-start' }}>
                          <Tooltip
                            title={
                              isIngestionPending
                                ? t('sidebar.project_pending_toggle_error', 'Finish the project creation flow before changing ingestion state')
                                : (isIngestionPaused
                                    ? t('sidebar.resume_project_ingestion', 'Resume ingestion')
                                    : t('sidebar.pause_project_ingestion', 'Pause ingestion'))
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onToggleCaseIngestionPause(caseData);
                                }}
                                disabled={isPendingCopy || !canToggleIngestion}
                                sx={{ opacity: (isMobile || isIngestionPaused || isIngestionPending) ? 1 : 0, '.MuiListItemButton-root:hover &': { opacity: 1 } }}
                              >
                                {isIngestionPaused && !isIngestionPending ? (
                                  <PlayArrowIcon fontSize="small" />
                                ) : (
                                  <PauseCircleOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" onClick={(e) => onCaseMenuOpen(e, caseData)} disabled={isPendingCopy} sx={{ opacity: 0, '.MuiListItemButton-root:hover &': { opacity: 1 } }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
              });
            })()}
          </List>
        </Box>

        {/* Footer: Create new project + Pagination */}
        <Divider sx={{ mt: 1 }} />
        <Box sx={{ px: 1, py: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{!selectedCollection && canAccessGlobalSettings && (<CreateMenuButton onCreateProject={handleCreateProject} primaryColor={themeColors.primary} hoverColor={themeColors.primaryLight} />)}</Box>
          <Box display={'flex'} justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onPageChange}
              size="small"
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': { color: themeColors.primary, '&.Mui-selected': { backgroundColor: themeColors.primary, color: '#fff', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(87, 87, 176, 0.2)' }, '&:hover': { backgroundColor: themeColors.lightBackground }, borderRadius: '8px' }, '& .MuiPagination-ul': { gap: '2px' }
              }}
            />
          </Box>
        </Box>
        <Divider />
        {canAccessGlobalSettings && (
          <Box sx={{ px: 2, pb: { xs: 1.5, sm: 2 }, pt: 1 }}>
            {!storageError ? (
              <Box sx={{ opacity: 0.85 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: themeColors.text, fontSize: '0.7rem' }}>
                    {t('sidebar.storage_label', 'STORAGE')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: themeColors.lightText, fontSize: '0.7rem' }}>
                    {isStorageLoading
                      ? '...'
                      : `${formatBytes(usedBytes)} / ${formatBytes(totalBytes)} (${Math.round(storageFreePercent)}% ${t('sidebar.storage_free', 'free')})`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LinearProgress
                    variant={isStorageLoading ? 'indeterminate' : 'determinate'}
                    value={storageUsedPercent}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      flex: 1,
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: themeColors.primary }
                    }}
                  />
                  {restrictToUserFolder && (
                    <Tooltip title={t('sidebar.storage_open', 'Open File Manager')}>
                      <IconButton
                        size="small"
                        onClick={onStorageOpen}
                        sx={{
                          color: themeColors.primary,
                          p: 0.5,
                          backgroundColor: 'rgba(87, 87, 176, 0.04)',
                          '&:hover': {
                            backgroundColor: 'rgba(87, 87, 176, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <FolderIcon sx={{ fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                {storageError}
              </Typography>
            )}
            {storageBrowserError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                {storageBrowserError}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Active Tasks Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(87, 87, 176, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${primaryColor}, ${selectedColor})`,
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          {t('sidebar.active_tasks_status') || 'Active Tasks Status'}
        </DialogTitle>
        <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0 }}>
          <ActiveTasksStatus />
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};

export default SidebarDrawer;
